import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { OpenGraphHead } from '../components/OpenGraphHead';
import { useImages } from '../context/ImageContext';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  author: string;
  excerpt?: string;
  content: string;
  featuredImage?: string;
  category: string;
  tags?: string;
  publishedAt: string | null;
  createdAt: string;
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug?: string; category?: string; postId?: string }>();
  const postSlug = slug;
  const { getImage } = useImages();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (postSlug) {
      fetchBlogPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postSlug]);

  const fetchBlogPost = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const response = await fetch(`${API_URL}/api/blog-posts/slug/${postSlug}?isPublished=true`);

      if (!response.ok) {
        throw new Error('Blog post not found');
      }

      const data = await response.json();

      if (!data.isPublished) {
        throw new Error('Blog post not found');
      }

      setPost(data);
    } catch (error: any) {
      console.error('Error fetching blog post:', error);
      setError(error.message || 'Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <p>Loading blog post...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <>
        <OpenGraphHead
          title="Blog Post Not Found | Alert1"
          description="The requested Alert1 blog post could not be found."
          pathOverride={postSlug ? `/blog/${postSlug}` : '/blog'}
          noIndex
        />
        <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
          <h1>Blog Post Not Found</h1>
          <p>{error || 'The blog post you are looking for does not exist.'}</p>
          <Link to="/blog" style={{ color: '#7eb3db', textDecoration: 'underline' }}>
            ← Back to Blog
          </Link>
        </div>
      </>
    );
  }

  const featuredImageUrl = post.featuredImage?.startsWith('http')
    ? post.featuredImage
    : post.featuredImage
    ? getImage(post.featuredImage)
    : undefined;

  return (
    <div className="blog-post-page">
      <OpenGraphHead
        title={post.title}
        description={post.excerpt || `Read ${post.title} on Alert1 Medical Alert News.`}
        imagePath={featuredImageUrl || '/logo512.png'}
        pathOverride={`/blog/${post.slug}`}
        imageAlt={post.title}
      />
      <section className="blog-hero-section">
        <div className="blog-hero-content">
          <h1 className="blog-hero-title">{post.title}</h1>
          <div className="blog-meta">
            <span className="blog-author">By {post.author}</span>
            <span className="blog-date">{formatDate(post.publishedAt || post.createdAt)}</span>
          </div>
          {post.excerpt && <p className="blog-subtitle">{post.excerpt}</p>}
        </div>
      </section>

      <section className="blog-content-section">
        <div className="blog-content-container">
          {featuredImageUrl && (
            <div className="blog-story-section" style={{ marginBottom: '2rem' }}>
              <img
                src={featuredImageUrl}
                alt={post.title}
                style={{ width: '100%', maxWidth: '800px', borderRadius: '8px' }}
              />
            </div>
          )}
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>
      </section>

      <div style={{ maxWidth: '900px', margin: '2rem auto 4rem', padding: '0 2rem' }}>
        <Link
          to="/blog"
          style={{
            color: '#7eb3db',
            textDecoration: 'none',
            fontSize: '1.1rem',
            fontWeight: 600
          }}
        >
          ← Back to All Posts
        </Link>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
